import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../../services/ApiService';
import VehicleCard from '../VehicleCard';
import { Button } from '../ui/Button';
import PageShell from '../ui/PageShell';
import { DirectoryBrowseLayout } from '../ui/DirectoryBrowseLayout';
import { DiscoverFiltersPanel, BUILD_PURPOSES } from './DiscoverFiltersPanel';
import type { DiscoveryFilters, PaginationMeta } from '../../types/discoveryBadges';
import {
  countActiveDiscoveryFilters,
  filtersFromParams,
  filtersToSearchParams,
} from '../../utils/discoveryUrlState';
import { vehicleProfilePath } from '../../utils/vehicleUrls';
import {
  DISCOVER_COLLECTIONS,
  collectionFilters,
  discoveryDestination,
  findCollection,
  DiscoverCollection,
} from './discoverCollections';
import './discover-collections.css';

type Result = { data: any[]; pagination: PaginationMeta; error?: string };
const EMPTY: Result = {
  data: [],
  pagination: { page: 1, limit: 20, total_count: 0, total_pages: 0 },
};
const SORTS = [
  ['updated_at', 'Recently updated'],
  ['created_at', 'Recently added'],
  ['documentation_score', 'Most documented'],
  ['horsepower', 'Highest listed horsepower'],
  ['modification_count', 'Most modifications'],
  ['favorite_count', 'Most favorited'],
  ['year', 'Newest model year'],
  ['make', 'Make / model'],
] as const;

function BuildCard({ vehicle: v }: { vehicle: any }) {
  const navigate = useNavigate();
  const href = vehicleProfilePath(v.owner_username || '', v.public_slug || String(v.id));
  return (
    <VehicleCard
      vehicle={{
        ...v,
        trim: v.trim || '',
        modification_count: v.modification_count || 0,
        display_name: v.display_name || `${v.year} ${v.make} ${v.model}`,
        created_at: v.created_at || '',
      }}
      showOwner
      href={href}
      onVehicleClick={() => undefined}
      onUserClick={(username) => navigate(`/${username}`)}
      discoveryBadges={v.badges}
      discoveryBadgeOverflow={v.badge_overflow}
      limitedDesignationPrimary={v.limited_designation_primary || null}
    />
  );
}

function CollectionShelf({
  collection,
  result,
  loading,
  filters,
  retry,
}: {
  collection: DiscoverCollection;
  result?: Result;
  loading: boolean;
  filters: DiscoveryFilters;
  retry: () => void;
}) {
  const rail = useRef<HTMLUListElement>(null);
  const href = discoveryDestination(filters, collection, true);
  const count = result?.pagination.total_count || 0;
  return (
    <section
      className="discover-shelf"
      id={`discover-${collection.id}`}
      aria-labelledby={`heading-${collection.id}`}
      aria-busy={loading}
    >
      <div className="discover-shelf__heading">
        <div>
          <span className="discover-eyebrow">{collection.mark} / THE COLLECTION</span>
          <h2 id={`heading-${collection.id}`}>{collection.title}</h2>
          <p>{collection.description}</p>
        </div>
        <div className="discover-shelf__actions">
          <Link to={href} aria-label={`See all ${collection.title.toLowerCase()}`}>
            See all <span aria-hidden>↗</span>
          </Link>
          <div className="discover-shelf__arrows">
            <button
              type="button"
              aria-label={`Scroll ${collection.title} left`}
              onClick={() =>
                rail.current?.scrollBy({
                  left: -290,
                  behavior: matchMedia('(prefers-reduced-motion: reduce)').matches
                    ? 'auto'
                    : 'smooth',
                })
              }
            >
              ←
            </button>
            <button
              type="button"
              aria-label={`Scroll ${collection.title} right`}
              onClick={() =>
                rail.current?.scrollBy({
                  left: 290,
                  behavior: matchMedia('(prefers-reduced-motion: reduce)').matches
                    ? 'auto'
                    : 'smooth',
                })
              }
            >
              →
            </button>
          </div>
        </div>
      </div>
      {loading ? (
        <div className="discover-loading" role="status">
          Finding builds…
        </div>
      ) : result?.error ? (
        <div className="discover-empty" role="alert">
          <p>This collection could not load. Your filters are still saved.</p>
          <Button onClick={retry}>Try again</Button>
        </div>
      ) : !result?.data.length ? (
        <div className="discover-empty">
          <p>No matching builds in this collection yet.</p>
          <Link to={discoveryDestination(filters, undefined, true)}>
            Search all matching builds →
          </Link>
        </div>
      ) : (
        <>
          <p className="discover-shelf__count">
            {Math.min(8, result.data.length)} of {count.toLocaleString()} matching builds
          </p>
          <ul
            ref={rail}
            className="collector-horizontal-rail discover-build-rail"
            aria-label={collection.title}
            tabIndex={0}
          >
            {result.data.slice(0, 8).map((v) => (
              <li key={v.id}>
                <BuildCard vehicle={v} />
              </li>
            ))}
            <li>
              <Link className="discover-more-card" to={href}>
                <span className="discover-eyebrow">KEEP EXPLORING</span>
                <span className="discover-more-card__symbol" aria-hidden>
                  ↗
                </span>
                <strong>{count > 8 ? 'See more builds' : 'Explore this collection'}</strong>
                <span>{collection.title}</span>
                <span className="discover-more-card__footer">
                  {count.toLocaleString()} matching builds · filters stay with you
                </span>
              </Link>
            </li>
          </ul>
        </>
      )}
    </section>
  );
}

export default function DiscoverCollectionsPage() {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => filtersFromParams(params), [params]);
  const collection = findCollection(params.get('collection'));
  const resultsView =
    params.get('view') === 'results' ||
    Boolean(collection) ||
    Boolean(filters.search) ||
    params.has('sort_by') ||
    Number(filters.page) > 1;
  const [draft, setDraft] = useState(filters);
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [retry, setRetry] = useState(0);
  const [shelves, setShelves] = useState<Record<string, Result>>({});
  const [result, setResult] = useState<Result>(EMPTY);
  const activeCount = countActiveDiscoveryFilters(filters);
  useEffect(() => {
    setDraft(filters);
    setSearchInput(filters.search || '');
  }, [filters]);
  useEffect(() => {
    let current = true;
    setLoading(true);
    const load = async () => {
      if (resultsView) {
        try {
          const next = await apiService.discoverVehicles(
            collection ? collectionFilters(filters, collection, false) : filters
          );
          if (current) setResult(next);
        } catch {
          if (current) setResult({ ...EMPTY, error: 'Builds could not load. Please try again.' });
        }
      } else {
        const responses = await Promise.all(
          DISCOVER_COLLECTIONS.map(async (c) => {
            try {
              return [
                c.id,
                await apiService.discoverVehicles(collectionFilters(filters, c)),
              ] as const;
            } catch {
              return [c.id, { ...EMPTY, error: 'Unavailable' }] as const;
            }
          })
        );
        if (current) setShelves(Object.fromEntries(responses));
      }
      if (current) setLoading(false);
    };
    void load();
    return () => {
      current = false;
    };
  }, [filters, collection, resultsView, retry]);

  const apply = (next: DiscoveryFilters, mode = resultsView, selected = collection) => {
    const target = new URLSearchParams(
      discoveryDestination(next, selected, mode).split('?')[1] || ''
    );
    if ((next.page || 1) > 1) target.set('page', String(next.page));
    setParams(target);
  };
  const activeChips = useMemo(() => {
    const chips: { key: string; label: string }[] = [];
    const add = (key: keyof DiscoveryFilters, label: string) => {
      const v = filters[key];
      if (v !== undefined && v !== null && v !== '' && v !== false) {
        chips.push({ key: String(key), label: `${label}: ${v}` });
      }
    };
    add('search', 'Search');
    add('doc_score_min', 'Documentation at least');
    add('make', 'Make');
    add('model', 'Model');
    add('trim', 'Trim');
    add('color', 'Color');
    add('chassis', 'Chassis');
    add('engine', 'Engine');
    add('transmission', 'Transmission');
    add('drivetrain', 'Drivetrain');
    add('build_status', 'Status');
    add('exterior_finish_type', 'Finish');
    add('exterior_finish_color', 'Finish color');
    add('exterior_finish_brand', 'Finish brand');
    add('mod_brand', 'Brand');
    add('mod_category', 'Category');
    add('event_slug', 'Event');
    add('award_title', 'Award');
    add('badge_category', 'Badge');
    add('club_slug', 'Club');
    if (filters.year_min || filters.year_max) {
      chips.push({
        key: 'year',
        label: `Year: ${filters.year_min || '…'}-${filters.year_max || '…'}`,
      });
    }
    if (filters.has_images === 'true' || filters.has_images === true) {
      chips.push({ key: 'has_images', label: 'Has photos' });
    }
    if (filters.has_modifications === 'true' || filters.has_modifications === true) {
      chips.push({ key: 'has_modifications', label: 'Has mods' });
    }
    if (filters.has_awards === 'true' || filters.has_awards === true) {
      chips.push({ key: 'has_awards', label: 'Has awards' });
    }
    if (filters.verified_club === 'true' || filters.verified_club === true) {
      chips.push({ key: 'verified_club', label: 'Verified club' });
    }
    if (filters.certified === 'true' || filters.certified === true) {
      chips.push({ key: 'certified', label: 'Certified builds' });
    }
    if (filters.spec_key && filters.spec_value) {
      chips.push({ key: 'spec', label: `${filters.spec_key}: ${filters.spec_value}` });
    }
    if (filters.build_purpose) {
      const labels = String(filters.build_purpose)
        .split(',')
        .map((p) => {
          const hit = BUILD_PURPOSES.find(([v]) => v === p.trim());
          return hit ? hit[1] : p.trim();
        })
        .filter(Boolean);
      chips.push({ key: 'build_purpose', label: `Purpose: ${labels.join(', ')}` });
    }
    if (filters.has_verified_dyno === 'true' || filters.has_verified_dyno === true) {
      chips.push({ key: 'has_verified_dyno', label: 'Verified dyno' });
    }
    if (filters.has_performance_record === 'true' || filters.has_performance_record === true) {
      chips.push({ key: 'has_performance_record', label: 'Performance records' });
    }
    if (filters.has_achievement === 'true' || filters.has_achievement === true) {
      chips.push({ key: 'has_achievement', label: 'Has achievements' });
    }
    if (filters.has_shop_contributor === 'true' || filters.has_shop_contributor === true) {
      chips.push({ key: 'has_shop_contributor', label: 'Shop built' });
    }
    if (filters.has_albums === 'true' || filters.has_albums === true) {
      chips.push({ key: 'has_albums', label: 'Has albums' });
    }
    if (filters.power_min || filters.power_max) {
      chips.push({
        key: 'power',
        label: `Power: ${filters.power_min || '0'}-${filters.power_max || 'any'} whp`,
      });
    }
    if (filters.ptw_min) chips.push({ key: 'ptw_min', label: `${filters.ptw_min}+ hp/ton` });
    if (filters.achievement) {
      chips.push({ key: 'achievement', label: `Achievement: ${filters.achievement}` });
    }
    return chips;
  }, [filters]);

  const clearFilter = (key: string) => {
    const next = { ...filters, page: 1 };
    const keys =
      key === 'year'
        ? ['year_min', 'year_max']
        : key === 'power'
          ? ['power_min', 'power_max']
          : key === 'spec'
            ? ['spec_key', 'spec_value']
            : [key];
    keys.forEach((k) => {
      delete (next as Record<string, unknown>)[k];
    });
    apply(next);
  };
  const clearAll = () => apply({ page: 1 });
  const applyDraft = () => {
    apply({ ...draft, search: filters.search, page: 1 });
    setMobileOpen(false);
  };
  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const next = filtersToSearchParams({
      ...filters,
      search: searchInput.trim() || undefined,
      page: 1,
    });
    next.set('view', 'results');
    setParams(next);
  };
  const backToOverview = () => {
    const next = filtersToSearchParams({ ...filters, search: undefined, page: 1 });
    next.delete('sort_by');
    next.delete('sort_order');
    setParams(next);
  };
  const title =
    collection?.title || (filters.search ? `Results for “${filters.search}”` : 'All builds');

  return (
    <PageShell
      width="full"
      contentClassName="discover-collections max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 flex-1 w-full"
    >
      <DirectoryBrowseLayout
        title="Discover"
        description={
          resultsView
            ? 'Filters narrow these results.'
            : 'Filters apply to every collection. Each keeps its own ranking.'
        }
        mobileTriggerLabel="Discover"
        sections={
          resultsView
            ? [{ id: 'discover-results', label: title }]
            : DISCOVER_COLLECTIONS.map((c) => ({ id: `discover-${c.id}`, label: c.title }))
        }
        activeFilterCount={activeCount}
        mobileOpen={mobileOpen}
        onMobileOpenChange={setMobileOpen}
        groups={[
          {
            id: 'filters',
            label: resultsView ? 'Filter results' : 'Filter every collection',
            children: <DiscoverFiltersPanel draft={draft} onChange={setDraft} />,
            footer: (
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setDraft({ search: filters.search })}>
                  Reset fields
                </Button>
                <Button onClick={applyDraft}>Apply filters</Button>
              </div>
            ),
          },
        ]}
      >
        <header className="discover-intro">
          <span className="discover-eyebrow">THE BUILDS. THE DETAILS. THE STORIES.</span>
          <h1>
            Find your next obsession<span aria-hidden>.</span>
          </h1>
          <p>
            A snapshot of what’s happening in the garages of Bilt:Spec. Find a favorite. Follow its
            story.
          </p>
        </header>
        <form onSubmit={submitSearch} className="discover-search">
          <label htmlFor="collection-search" className="sr-only">
            Search all builds
          </label>
          <input
            id="collection-search"
            className="input"
            placeholder="Try Porsche, RB26, track build…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <Button type="submit" aria-label="Search builds">
            Search <span aria-hidden>↗</span>
          </Button>
        </form>
        <div className="discover-filter-context">
          <p>
            {resultsView
              ? 'Filters apply to the results below.'
              : 'Filters apply to all five collections; rankings stay the same.'}
          </p>
          <Button variant="ghost" onClick={() => setMobileOpen(true)} className="lg:hidden">
            Filters{activeCount ? ` (${activeCount})` : ''}
          </Button>
          {!resultsView && (
            <Link to={discoveryDestination(filters, undefined, true)}>Browse all builds →</Link>
          )}
        </div>
        {activeChips.length > 0 && (
          <div className="discover-chips" aria-label="Applied filters">
            {activeChips.map((c) => (
              <button
                type="button"
                key={c.key}
                className="filter-chip"
                onClick={() => clearFilter(c.key)}
                aria-label={`Remove ${c.label}`}
              >
                {c.label} <span aria-hidden>×</span>
              </button>
            ))}
            <button type="button" onClick={clearAll}>
              Clear filters
            </button>
          </div>
        )}
        {!resultsView ? (
          DISCOVER_COLLECTIONS.map((c) => (
            <CollectionShelf
              key={c.id}
              collection={c}
              result={shelves[c.id]}
              loading={loading}
              filters={filters}
              retry={() => setRetry((v) => v + 1)}
            />
          ))
        ) : (
          <section id="discover-results" className="discover-results" aria-busy={loading}>
            <button type="button" className="discover-back" onClick={backToOverview}>
              ← All collections
            </button>
            <div className="discover-results__heading">
              <div>
                <h2>{title}</h2>
                <p>
                  {collection?.description ||
                    'Find the build you have in mind, then make it your own collection.'}
                </p>
                <p role="status">
                  {loading
                    ? 'Finding builds…'
                    : `${result.pagination.total_count.toLocaleString()} matching builds`}
                </p>
              </div>
              {!collection && (
                <label>
                  Sort results
                  <select
                    className="select-surface"
                    value={filters.sort_by}
                    onChange={(e) =>
                      apply({
                        ...filters,
                        sort_by: e.target.value,
                        sort_order: e.target.value === 'make' ? 'asc' : 'desc',
                        page: 1,
                      })
                    }
                  >
                    {SORTS.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
            {loading ? (
              <div className="discover-loading" role="status">
                Loading the collection…
              </div>
            ) : result.error ? (
              <div className="discover-empty" role="alert">
                <p>{result.error}</p>
                <Button onClick={() => setRetry((v) => v + 1)}>Try again</Button>
              </div>
            ) : !result.data.length ? (
              <div className="discover-empty">
                <h3>No builds match yet</h3>
                <p>Try a broader search or remove a filter. Your collection is still here.</p>
                <Button onClick={clearAll}>Clear filters</Button>
              </div>
            ) : (
              <div className="discover-results-grid">
                {result.data.map((v) => (
                  <BuildCard key={v.id} vehicle={v} />
                ))}
              </div>
            )}
            {!loading && !result.error && result.pagination.total_pages > 1 && (
              <nav className="discover-pagination" aria-label="Results pages">
                <Button
                  variant="secondary"
                  disabled={result.pagination.page <= 1}
                  onClick={() => apply({ ...filters, page: result.pagination.page - 1 })}
                >
                  Previous
                </Button>
                <span>
                  Page {result.pagination.page} of {result.pagination.total_pages}
                </span>
                <Button
                  variant="secondary"
                  disabled={result.pagination.page >= result.pagination.total_pages}
                  onClick={() => apply({ ...filters, page: result.pagination.page + 1 })}
                >
                  Next
                </Button>
              </nav>
            )}
          </section>
        )}
      </DirectoryBrowseLayout>
    </PageShell>
  );
}
